import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../api";
import type { Dayjs } from "dayjs";

export type BarcodeBankState = "available" | "assigned" | "voided";

export interface BarcodeBankRow {
  barcode: string;
  voucher_type_id: number;
  voucher_type_name: string;
  state: BarcodeBankState;
  assigned_voucher_id: string | null;
  imported_at: Dayjs;
  imported_by_user_id: number;
}

export interface KpiRow {
  voucher_type_id: number;
  name: string;
  available_count: number;
  low_stock_threshold: number;
}

export interface BarcodeBankFilters {
  voucher_type_id?: number | string;
  state?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface BarcodeBankResponse {
  items: BarcodeBankRow[];
  total: number;
  page: number;
  per_page: number;
  kpis: KpiRow[];
}

export function useBarcodeBank(filters: BarcodeBankFilters) {
  const params = new URLSearchParams();
  if (filters.voucher_type_id) params.set("voucher_type_id", String(filters.voucher_type_id));
  if (filters.state) params.set("state", filters.state);
  if (filters.search) params.set("search", filters.search);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  if (filters.per_page) params.set("per_page", String(filters.per_page));
  const qs = params.toString();

  return useQuery<BarcodeBankResponse>({
    queryKey: ["barcode-bank", filters],
    queryFn: () => apiGet(`/barcode-bank${qs ? `?${qs}` : ""}`),
  });
}

export interface GenerateBarcodesPayload {
  voucher_type_id: number;
  count: number;
}

export interface ImportResult {
  inserted: number;
  skipped: number;
  rejected: { barcode: string; reason: string }[];
}

export function useGenerateBarcodes() {
  const qc = useQueryClient();
  return useMutation<ImportResult, unknown, GenerateBarcodesPayload>({
    mutationFn: (payload) => apiPost("/barcode-bank/generate", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["barcode-bank"] }),
  });
}

export interface ImportRange {
  from: string;
  to: string;
  voucher_type_id: number;
}

export function useImportBarcodes() {
  const qc = useQueryClient();
  return useMutation<ImportResult, unknown, ImportRange[]>({
    mutationFn: (ranges) => apiPost("/barcode-bank/import", ranges),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["barcode-bank"] }),
  });
}

export function useVoidBarcode() {
  const qc = useQueryClient();
  return useMutation<unknown, unknown, string>({
    mutationFn: (barcode) =>
      apiPost(`/barcode-bank/${encodeURIComponent(barcode)}/void`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["barcode-bank"] }),
  });
}
